import type { Schema } from '@lemonade-hq/chat-engine';
import type { ConditionalItem, OptionallyConditionalItem } from '@lemonade-hq/chat-wizard-common-definitions';
import type { InterpolationString } from './utils/interpolate';

export const REPLACE_WITH_MAP_DEFAULT_KEY = '_default_';
export const REPLACE_WITH_MAP_WILDCARD = '_wildcard_';

export interface MappedInterpolation<
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- keeping here until the NonWildcardRecordPath issue is solved
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> {
  /** The output pattern */
  // TODO: use NonWildcardRecordPath<TSchema> instead of string - large schemas result in too many type instantiations
  readonly pattern: NoInfer<InterpolationString<string, NoInfer<TOutput>>>;
  /** Map values from the answers to the pattern */
  readonly mapValues?: Partial<Record<TAnswersAttributes, Partial<Record<string, string>>>>;
  /** After interpolation, replace the value with the mapped value */
  readonly replaceWithMap?: Partial<Record<string, string> & { readonly [REPLACE_WITH_MAP_DEFAULT_KEY]?: string }>;
}

export type ConditionalMappedInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> = [
  MappedInterpolation<TSchema, TAnswersAttributes, TOutput>,
  // TODO: find a way to make conditional items work with TOutput
  ...ConditionalItem<MappedInterpolation<TSchema, TAnswersAttributes, string>>[],
];

export type MappedInterpolationOrInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> = InterpolationString<string, TOutput> | MappedInterpolation<TSchema, TAnswersAttributes, TOutput>;

export type UniclientMappedInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> =
  | ConditionalMappedInterpolation<TSchema, TAnswersAttributes, TOutput>
  // TODO: use NonWildcardRecordPath<TSchema> instead of string - large schemas result in too many type instantiations
  | MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>;

export type ConditionalMappedInterpolationOrInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> = [
  MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>,
  ...ConditionalItem<MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>>[],
];

export type UniclientConditionalMappedInterpolationOrInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> =
  | ConditionalMappedInterpolationOrInterpolation
  | MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>;

export type OptionalConditionalMappedInterpolationOrInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> = [
  OptionallyConditionalItem<MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>>,
  ...ConditionalItem<MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>>[],
];

export type UniclientOptionalConditionalMappedInterpolationOrInterpolation<
  TSchema extends Schema = Schema,
  TAnswersAttributes extends string = string,
  TOutput extends string = string,
> =
  | MappedInterpolationOrInterpolation<TSchema, TAnswersAttributes, TOutput>
  | OptionalConditionalMappedInterpolationOrInterpolation;
